<script lang="ts" setup>
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { debouncedWatch } from '@vueuse/core';
import { nextTick, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { VButton } from '@shared/component/VButton';
import { isEnterKey } from '@tools/keyboard';

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    clear?: boolean;
    autoFocus?: boolean;
    onFocus?: boolean;
    debounce?: number;
    showSearchButton?: boolean;
  }>(),
  {
    placeholder: 'Zoeken...',
    autoFocus: true,
    debounce: 300,
    showSearchButton: false,
  },
);

const emit = defineEmits<{ input: [string]; onTab: []; onEnter: []; search: [] }>();

const model = defineModel<string>({ default: '' });

const val = ref<string>('');
const root = ref();

watch(
  () => props.autoFocus,
  () => {
    if (!props.autoFocus) return;
    nextTick(() => root.value?.focus());
  },
  { immediate: true },
);

watch(
  () => props.onFocus,
  () => {
    nextTick(() => root.value?.focus());
  },
);

debouncedWatch(
  () => val.value,
  () => {
    model.value = val.value;
    emit('input', val.value);
  },
  { debounce: props.debounce },
);

watch(
  () => props.clear,
  () => clearSearch(),
);

function clearSearch() {
  val.value = '';
  model.value = val.value;
  emit('input', val.value);
}

function onKeyDown(event: KeyboardEvent) {
  if (event.code === 'Tab') emit('onTab');
  if (isEnterKey(event)) emit('onEnter');
}

function onSearchClick() {
  emit('search');
}

watch(model, (newValue) => (val.value = newValue), {
  immediate: true,
});

const searchLabelId = uuidv4();
</script>

<template>
  <label
    class="flex cursor-text gap-2 rounded-lg border border-solid border-grey-250 bg-white px-4 py-2 transition-colors focus-within:border-grey-400"
    :for="searchLabelId"
    v-bind="$attrs"
  >
    <span v-if="!showSearchButton" class="flex w-4 items-center justify-center text-primary-900">
      <FontAwesomeIcon icon="fa-regular fa-search" class="w-3.5" />
    </span>
    <input
      :id="searchLabelId"
      class="grow border-none bg-inherit py-px focus-visible:outline-none"
      ref="root"
      type="search"
      autocomplete="on"
      :placeholder="placeholder"
      v-model="val"
      data-testid="v-search"
      @keydown="onKeyDown"
    />
    <span
      v-if="val"
      @click="clearSearch"
      class="flex cursor-pointer items-center justify-center text-grey"
      data-testid="v-search-clear"
    >
      <FontAwesomeIcon icon="fa-regular fa-times" class="font-bold" />
    </span>
    <VButton
      v-if="showSearchButton"
      hide-shadow
      type="plain"
      class="-my-2 -mr-4 rounded-lg rounded-l-none border-0"
      prepend-icon="far fa-search"
      @click="onSearchClick"
      data-testid="v-search-button"
    />
  </label>
</template>
